(() => {
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.items = this.querySelector('[data-cart-items]');
      this.status = this.querySelector('[data-cart-status]');
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-cart-open]');
        if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.open(trigger);
      }, options);
      document.addEventListener('cart:updated', event => {
        if (event.detail?.open) this.open();
        else this.refresh();
      }, options);
      this.querySelector('[data-cart-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => this.trigger?.focus({ preventScroll: true }), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right) this.dialog.close();
      }, options);
      this.items.addEventListener('click', event => {
        const line = event.target.closest('[data-cart-key]');
        if (!line) return;
        if (event.target.closest('[data-cart-remove]')) this.change(line.dataset.cartKey, 0);
        const step = event.target.closest('[data-cart-change]');
        if (step) this.change(line.dataset.cartKey, Math.max(0, Number(line.dataset.quantity) + Number(step.dataset.cartChange)));
      }, options);
    }

    disconnectedCallback() { this.controller?.abort(); }

    async open(trigger) {
      if (trigger) this.trigger = trigger;
      else this.trigger = document.querySelector('[data-cart-open]');
      document.querySelector('.quick-shop__dialog[open]')?.close();
      if (!this.dialog.open) this.dialog.showModal();
      await this.refresh();
    }

    async refresh() {
      this.status.hidden = true;
      this.dialog.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(this.dataset.cartError);
        this.render(await response.json());
      } catch (error) {
        this.status.textContent = error.message || this.dataset.cartError;
        this.status.hidden = false;
      } finally { this.dialog.removeAttribute('aria-busy'); }
    }

    async change(key, quantity) {
      if (this.pending) return;
      this.pending = true;
      this.dialog.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${this.dataset.cartUrl}/change.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || this.dataset.cartError);
        this.status.hidden = true;
        this.render(cart);
      } catch (error) {
        this.status.textContent = error.message || this.dataset.cartError;
        this.status.hidden = false;
      } finally { this.pending = false; this.dialog.removeAttribute('aria-busy'); }
    }

    formatMoney(cents, currency) {
      return new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency }).format(cents / 100);
    }

    render(cart) {
      this.items.replaceChildren();
      const currency = cart.currency || this.dataset.currency;
      if (!cart.items.length) this.items.append(this.querySelector('[data-cart-empty-template]').content.cloneNode(true));
      cart.items.forEach(item => {
        const line = this.querySelector('[data-cart-line-template]').content.cloneNode(true);
        const article = line.querySelector('article');
        article.dataset.cartKey = item.key;
        article.dataset.quantity = item.quantity;
        article.querySelectorAll('[data-line-link], [data-line-title]').forEach(link => { link.href = item.url; });
        const image = article.querySelector('[data-line-image]');
        if (item.image) { image.src = item.image; image.alt = item.product_title; }
        else image.hidden = true;
        article.querySelector('[data-line-title]').textContent = item.product_title;
        const variant = article.querySelector('[data-line-variant]');
        variant.textContent = item.variant_title === 'Default Title' ? '' : item.variant_title || '';
        article.querySelector('[data-line-price]').textContent = this.formatMoney(item.final_line_price, currency);
        article.querySelector('[data-line-quantity]').textContent = item.quantity;
        this.items.append(line);
      });
      this.querySelector('[data-drawer-count]').textContent = `(${cart.item_count})`;
      this.querySelector('[data-cart-subtotal]').textContent = this.formatMoney(cart.total_price, currency);
      this.querySelector('.cart-drawer__footer').hidden = !cart.items.length;
      document.querySelectorAll('[data-cart-count]').forEach(count => {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      });
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
})();
